import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  CheckCircle2, Star, Users, Clock, Globe, Award, ChevronDown, ChevronUp,
  MapPin, ExternalLink, Share2, Copy, BookOpen, GraduationCap, Loader2
} from 'lucide-react';
import axios from 'axios';
import AdBlock from '../components/AdBlock';
import { formatMoney } from '../lib/currency';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;


function StarDisplay({ rating, size = 'sm' }) {
  const sz = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sz} ${star <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-300'}`}
        />
      ))}
    </div>
  );
}

function RatingBar({ stars, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm w-3">{stars}</span>
      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
    </div>
  );
}

export default function CourseDetailsPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedModules, setExpandedModules] = useState({});
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    fetchCourse();
    if (isAuthenticated) checkEnrollment();
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkEnrollment = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/enrollments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if ((res.data || []).some(e => e.course_id === courseId)) {
        setEnrolled(true);
      }
    } catch { /* silent */ }
  };

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/courses/${courseId}`);
      setCourse(res.data);
    } catch {
      setCourse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/courses/${courseId}`);
      return;
    }
    const isPaid = course && !course.is_free && Number(course.price) > 0;
    setEnrolling(true);
    try {
      if (isPaid) {
        const res = await axios.post(
          `${API_URL}/api/courses/${courseId}/checkout`,
          { origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.url) window.location.href = res.data.url;
        return;
      }
      await axios.post(
        `${API_URL}/api/enrollments`,
        { course_id: courseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnrolled(true);
      toast.success('Successfully enrolled! Check your dashboard for this course.');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('Already enrolled')) {
        setEnrolled(true);
        toast.info('You are already enrolled in this course.');
      } else {
        toast.error(detail || 'Could not complete enrollment. Please try again.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const toggleModule = (idx) => {
    setExpandedModules((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success('Link copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#003366]" />
      </div>
    );
  }

  if (!course) return null;

  // Normalise field names — stored docs may use syllabus/outcomes instead of curriculum/what_you_learn
  const whatYouLearn = course.what_you_learn || course.outcomes || [];
  const curriculum = course.curriculum || (course.syllabus || []).map((title, i) => ({ title, lessons: 0, duration: '' }));
  const reviews = course.reviews || [];

  const ratingCounts = [5, 4, 3, 2, 1].map((s) => ({
    stars: s,
    count: reviews.filter((r) => r.rating === s).length,
  }));
  const totalReviews = reviews.length;

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-[#003366]">Home</Link>
            <span>/</span>
            <Link to="/courses" className="hover:text-[#003366]">Courses</Link>
            <span>/</span>
            <span className="text-foreground font-medium truncate max-w-[300px]">{course.title}</span>
          </nav>
        </div>
      </div>

      <AdBlock position="course-detail-sidebar" showEmpty />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* LEFT COLUMN */}
          <div className="flex-1 min-w-0">
            {/* Hero Section */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              {/* Institution Badge */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-[#003366]" />
                </div>
                <span className="text-sm font-medium text-[#003366]">{course.institution_name}</span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{course.title}</h1>
              <p className="text-muted-foreground mb-4 leading-relaxed">{course.description}</p>

              {/* Rating + enrollment */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-amber-500">{course.average_rating?.toFixed(1)}</span>
                  <StarDisplay rating={course.average_rating || 0} />
                  <span className="text-sm text-muted-foreground">({(course.enrollments_count || 0).toLocaleString()} students)</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>{(course.enrollments_count || 0).toLocaleString()} enrolled</span>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {course.category && (
                  <Badge className="bg-blue-100 text-blue-700 border-0">{course.category}</Badge>
                )}
                {course.level && (
                  <Badge className="bg-purple-100 text-purple-700 border-0 capitalize">{course.level}</Badge>
                )}
                {course.delivery_mode && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 capitalize">{course.delivery_mode}</Badge>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="flex border-b">
                {['overview', 'curriculum', 'reviews'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-4 text-sm font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? 'text-[#003366] border-b-2 border-[#003366]'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && (
                  <div className="space-y-8">
                    {/* Description */}
                    <div>
                      <h2 className="text-xl font-semibold mb-3">About this course</h2>
                      {course.description?.split('. ').map((para, i) => para.trim() && (
                        <p key={i} className="text-muted-foreground mb-3 leading-relaxed">{para.trim()}{para.endsWith('.') ? '' : '.'}</p>
                      ))}
                    </div>

                    {/* What you'll learn */}
                    {whatYouLearn.length > 0 && (
                      <div>
                        <h2 className="text-xl font-semibold mb-4">What you'll learn</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {whatYouLearn.map((item, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                              <span className="text-sm text-muted-foreground">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Requirements */}
                    {course.requirements?.length > 0 && (
                      <div>
                        <h2 className="text-xl font-semibold mb-4">Requirements</h2>
                        <ul className="space-y-2">
                          {course.requirements.map((req, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#003366] mt-2 shrink-0" />
                              <span className="text-sm text-muted-foreground">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* About provider */}
                    <div>
                      <h2 className="text-xl font-semibold mb-4">About the provider</h2>
                      <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center shrink-0">
                          <GraduationCap className="w-6 h-6 text-[#003366]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{course.institution_name}</h3>
                          {course.institution_location && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                              <MapPin className="w-4 h-4" />
                              <span>{course.institution_location}</span>
                            </div>
                          )}
                          {course.institution_website && (
                            <a
                              href={course.institution_website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-[#003366] hover:underline mt-2"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Visit website
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* CURRICULUM TAB */}
                {activeTab === 'curriculum' && (
                  <div>
                    <h2 className="text-xl font-semibold mb-2">Course Curriculum</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      {curriculum.length} modules &bull; {curriculum.reduce((a, m) => a + (m.lessons || 0), 0)} lessons &bull; {course.duration} total
                    </p>
                    {curriculum.length === 0 && (
                      <p className="text-muted-foreground text-sm py-8 text-center">No curriculum details available for this course.</p>
                    )}
                    <div className="space-y-3">
                      {curriculum.map((module, idx) => (
                        <div key={idx} className="border rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleModule(idx)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-7 h-7 rounded-full bg-[#003366]/10 flex items-center justify-center text-xs font-bold text-[#003366]">
                                {idx + 1}
                              </div>
                              <span className="font-medium">{module.title}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-muted-foreground hidden sm:block">
                                {module.lessons} lessons &bull; {module.duration}
                              </span>
                              {expandedModules[idx] ? (
                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </button>
                          {expandedModules[idx] && (
                            <div className="border-t bg-gray-50 px-4 py-3">
                              <div className="flex items-center gap-4 text-sm text-muted-foreground sm:hidden mb-2">
                                <span>{module.lessons} lessons</span>
                                <span>{module.duration}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                This module contains {module.lessons} lessons covering {module.title.toLowerCase()} topics over {module.duration}.
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* REVIEWS TAB */}
                {activeTab === 'reviews' && (
                  <div>
                    <h2 className="text-xl font-semibold mb-6">Student Reviews</h2>
                    {totalReviews > 0 ? (
                      <>
                        {/* Rating Summary */}
                        <div className="flex flex-col sm:flex-row items-start gap-8 mb-8 p-6 bg-gray-50 rounded-xl">
                          <div className="text-center">
                            <div className="text-5xl font-bold text-[#003366]">{course.average_rating?.toFixed(1)}</div>
                            <StarDisplay rating={course.average_rating || 0} size="md" />
                            <div className="text-sm text-muted-foreground mt-1">{totalReviews} reviews</div>
                          </div>
                          <div className="flex-1 w-full space-y-2">
                            {ratingCounts.map(({ stars, count }) => (
                              <RatingBar key={stars} stars={stars} count={count} total={totalReviews} />
                            ))}
                          </div>
                        </div>

                        {/* Individual Reviews */}
                        <div className="space-y-6">
                          {reviews.map((review) => (
                            <div key={review.id} className="pb-6 border-b last:border-0">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-[#003366] flex items-center justify-center text-white text-sm font-medium">
                                    {review.reviewer_name?.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="font-medium text-sm">{review.reviewer_name}</div>
                                    <div className="text-xs text-muted-foreground">{review.date}</div>
                                  </div>
                                </div>
                                <StarDisplay rating={review.rating} />
                              </div>
                              <p className="text-sm text-muted-foreground leading-relaxed ml-12">{review.comment}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No reviews yet. Be the first to review!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="w-full lg:w-80 shrink-0">
            <div className="sticky top-20 space-y-4">
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  {/* Price */}
                  <div className="mb-5">
                    {course.is_free ? (
                      <div className="flex items-center gap-2">
                        <span className="text-4xl font-bold text-emerald-600">Free</span>
                        <Badge className="bg-emerald-100 text-emerald-700 border-0">No cost</Badge>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline gap-3">
                          <span className="text-4xl font-bold text-[#003366]">{formatMoney(course.price, course.currency)}</span>
                          {course.original_price && (
                            <span className="text-xl text-muted-foreground line-through">{formatMoney(course.original_price, course.currency)}</span>
                          )}
                        </div>
                        {course.original_price && (
                          <Badge className="bg-[#E11D48]/10 text-[#E11D48] border-0 mt-1">
                            Save {formatMoney(course.original_price - course.price, course.currency)} ({Math.round(((course.original_price - course.price) / course.original_price) * 100)}% off)
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={handleEnroll}
                    disabled={enrolling || enrolled}
                    className="w-full h-12 text-base font-semibold bg-emerald-600 hover:bg-emerald-700"
                  >
                    {enrolling ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Enrolling…</>
                    ) : enrolled ? (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />Enrolled</>
                    ) : course.is_free ? 'Start Free Course' : 'Enroll Now'}
                  </Button>
                  {enrolled && (
                    <Link to="/university/dashboard" className="block mt-2">
                      <Button variant="outline" className="w-full h-10">Go to Dashboard</Button>
                    </Link>
                  )}

                  {/* Course Highlights */}
                  <div className="mt-6 space-y-3">
                    <h4 className="font-semibold text-sm">This course includes:</h4>
                    {[
                      course.duration && `${course.duration} of content`,
                      course.level && `${course.level.charAt(0).toUpperCase() + course.level.slice(1)} level`,
                      course.delivery_mode && `${course.delivery_mode.charAt(0).toUpperCase() + course.delivery_mode.slice(1)} delivery`,
                      'Certificate of completion',
                      'Lifetime access',
                      'Mobile friendly',
                    ].filter(Boolean).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* Share */}
                  <div className="mt-6 pt-5 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Share2 className="w-4 h-4" />
                        <span>Share this course</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyLink}
                        className="h-8 text-xs gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copy link
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
